import { CA_TAX_RATE, CA_INSURANCE_RATE, monthlyPI } from './mortgage';

/**
 * Max affordable price via the classic 28/36 DTI rule, solved in closed form.
 * Housing budget = min(28% of gross monthly income, 36% minus other debts).
 * The 28/36 front-end ratio is measured against full PITI, so the monthly cost
 * of price P with down D at monthly factor f includes taxes AND insurance:
 *   (P − D)·f + P·(taxMonthly + insMonthly)
 *   ⇒  P = (budget + D·f) / (f + taxMonthly + insMonthly)
 */
export function maxAffordablePrice(
  annualIncome: number,
  monthlyDebts: number,
  downPayment: number,
  annualRatePct: number,
  years = 30
): { price: number; monthlyBudget: number } | null {
  if (annualIncome <= 0) return null;
  const gross = annualIncome / 12;
  const budget = Math.min(0.28 * gross, 0.36 * gross - monthlyDebts);
  if (budget <= 0) return null;

  // Per-dollar monthly principal+interest factor.
  const f = monthlyPI(1, annualRatePct, years);
  const taxMonthly = CA_TAX_RATE / 12;
  const insMonthly = CA_INSURANCE_RATE / 12;
  const price = (budget + downPayment * f) / (f + taxMonthly + insMonthly);
  if (price <= downPayment) return null;

  return { price: Math.round(price / 1000) * 1000, monthlyBudget: Math.round(budget) };
}
